import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Stack,
  Sparkle,
  Certificate,
  ArrowsClockwise,
  Lightning,
  ArrowRight,
  CheckCircle,
  ShieldCheck,
  TrendUp,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AFFILIATE_FALLBACK = "https://www.entrepedia.co/master-library?ref=france#pricing";

const AVATARS = [
  "https://www.entrepedia.co/_next/image?url=https%3A%2F%2Fentrepedia-products.com%2Fwebsite%2Fimages%2Fh6lRYa4Au4bhHo74dLrOKVmEXM.jpg&w=64&q=75",
  "https://www.entrepedia.co/_next/image?url=https%3A%2F%2Fentrepedia-products.com%2Fwebsite%2Fimages%2FyAv1sMKHDkbozLJIZOpXbvIi5MY.png&w=64&q=75",
  "https://www.entrepedia.co/_next/image?url=https%3A%2F%2Fentrepedia-products.com%2Fwebsite%2Fimages%2FFzkruTsK9Cw1GQ7SXLPP6pAXD2Y.jpg&w=64&q=75",
  "https://www.entrepedia.co/_next/image?url=https%3A%2F%2Fentrepedia-products.com%2Fwebsite%2Fimages%2FmR5rFLRlv4jfISpbldKdeeRRDE.webp&w=64&q=75",
  "https://www.entrepedia.co/_next/image?url=https%3A%2F%2Fentrepedia-products.com%2Fwebsite%2Fimages%2FXaAXXMdWYEn9rAhC953nSpdwxU.png&w=64&q=75",
];

const FEATURES = [
  { icon: Stack, label: "1000+ formations à vie" },
  { icon: Sparkle, label: "Business · IA · Marketing · E-commerce · Mindset" },
  { icon: Certificate, label: "Droits de revente inclus (PLR)" },
  { icon: ArrowsClockwise, label: "Mises à jour à vie" },
  { icon: Lightning, label: "Accès immédiat" },
];

const QUICK_BENEFITS = [
  "100% des bénéfices",
  "Droits de revente",
  "Mises à jour à vie",
];

const LOGOS = ["Cisco", "Harvard", "PwC", "WPP", "Speechify", "Keller Williams"];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function useCountdown(hours = 47, minutes = 59) {
  // Static — countdown removed for trust/compliance reasons.
  return { h: hours, m: minutes, s: 0 };
}

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Entre une adresse email valide.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { email });
      const url = res?.data?.redirect_url || AFFILIATE_FALLBACK;
      toast.success("Code envoyé. Redirection…");
      setTimeout(() => {
        window.location.href = url;
      }, 600);
    } catch (err) {
      console.error(err);
      toast.message("Redirection en cours…");
      setTimeout(() => {
        window.location.href = AFFILIATE_FALLBACK;
      }, 400);
    } finally {
      setLoading(false);
    }
  };

  const scrollToCTA = () => {
    document
      .querySelector('[data-testid="cta-section"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <main
      data-testid="landing-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] text-white"
    >
      {/* Subtle grid + radial glow background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.4) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="absolute left-1/2 top-[-10%] h-[600px] w-[800px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,_rgba(239,68,68,0.18)_0%,_transparent_60%)] blur-3xl" />
      </div>

      {/* Static promo bar */}
      <div
        data-testid="urgency-bar"
        className="relative z-20 w-full border-b border-[#ef2b2b]/20 bg-[#ef2b2b]/[0.08] backdrop-blur-sm"
      >
        <div className="mx-auto flex max-w-3xl items-center justify-center gap-3 px-6 py-2.5 text-center text-xs font-medium text-zinc-200 sm:text-sm">
          <span aria-hidden="true">🎁</span>
          <span>
            <span className="font-bold text-white">-10% exclusif</span>{" "}
            <span className="text-zinc-400">·</span> Ton accès à vie
            <span className="hidden sm:inline">
              <span className="text-zinc-400"> · </span>
              <span className="text-[#ef2b2b]">Code privé par email</span>
            </span>
          </span>
        </div>
      </div>

      <div className="relative z-10 mx-auto flex max-w-3xl flex-col gap-20 px-6 py-12 pb-32 sm:gap-24 sm:px-8 sm:py-16 sm:pb-24">
        {/* Brand mark */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="flex items-center justify-between"
        >
          <div
            data-testid="brand-mark"
            className="flex items-center gap-1 font-display text-base font-black uppercase tracking-tight text-white"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_digital-products-fr/artifacts/4a5ryr9p_1720010162623-Logo-white-red.png"
              alt="Entrepedia"
              className="h-7 w-auto sm:h-8"
              loading="eager"
            />
            <span className="-ml-1 text-[#ef2b2b] sm:text-lg">.FR</span>
          </div>
        </motion.div>

        {/* HERO */}
        <section
          data-testid="hero-section"
          className="flex flex-col items-center gap-7 text-center"
        >
          <motion.span
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="inline-flex items-center rounded-full bg-[#ef2b2b] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-[0_0_30px_-5px_rgba(239,43,43,0.6)]"
          >
            Master Library — France
          </motion.span>
          <motion.h1
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-headline"
            className="font-display text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-[5rem]"
          >
            1000+ formations business.
            <br />
            <span className="text-[#ef2b2b]">À vie.</span>
          </motion.h1>
          <motion.p
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="hero-subline"
            className="max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          >
            La plus grande bibliothèque de produits digitaux au monde.
            Téléchargeables. Revendables à ton nom.
          </motion.p>

          <motion.ul
            custom={3}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-2"
          >
            {QUICK_BENEFITS.map((b) => (
              <li key={b} className="flex items-center gap-2 text-sm text-zinc-300">
                <CheckCircle size={18} weight="fill" className="text-[#ef2b2b]" />
                {b}
              </li>
            ))}
          </motion.ul>

          <motion.button
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            onClick={scrollToCTA}
            data-testid="hero-cta"
            className="group mt-2 inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#ef2b2b] px-8 text-sm font-black uppercase tracking-wider text-white shadow-[0_0_40px_-8px_rgba(239,43,43,0.8)] transition-all hover:bg-[#dc1f1f] hover:scale-[1.02]"
          >
            Recevoir mon code -10%
            <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </motion.button>

          {/* Mini social proof under CTA */}
          <motion.div
            custom={5}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="flex items-center gap-3"
          >
            <div className="flex -space-x-2">
              {AVATARS.slice(0, 5).map((src, i) => (
                <span
                  key={i}
                  className="relative inline-block h-7 w-7 overflow-hidden rounded-full ring-2 ring-[#0a0a0a]"
                >
                  <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                </span>
              ))}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-zinc-400">
              <span className="flex items-center gap-0.5 text-[#ef2b2b]">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l2.97 6.02L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14 2 9.27l7.03-1.25L12 2z" />
                  </svg>
                ))}
              </span>
              <span>+20 000 créateurs · 50 pays</span>
            </div>
          </motion.div>
        </section>

        {/* LOGO STRIP — credibility */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="logo-strip"
          className="flex flex-col items-center gap-5 border-y border-white/5 py-8"
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">
            Utilisé par les pros de
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 sm:gap-x-12">
            {LOGOS.map((logo) => (
              <span
                key={logo}
                className="font-display text-base font-black uppercase tracking-wider text-zinc-400 transition-colors hover:text-white sm:text-lg"
              >
                {logo}
              </span>
            ))}
          </div>
        </motion.section>

        {/* OBJECTION — PLR + Sales angle */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
          data-testid="objection-section"
          className="relative overflow-hidden rounded-2xl border border-[#ef2b2b]/30 bg-gradient-to-br from-[#1a0a0a] via-[#121212] to-[#0a0a0a] p-8 shadow-[0_0_80px_-15px_rgba(239,43,43,0.35)] sm:p-12"
        >
          <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[#ef2b2b]/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-[#ef2b2b]/10 blur-3xl" />

          <div className="relative flex flex-col gap-6">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#ef2b2b]/40 bg-[#ef2b2b]/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.25em] text-[#ef2b2b]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ef2b2b] shadow-[0_0_8px_rgba(239,43,43,0.9)]" />
              L'opportunité 2026
            </div>

            <h2
              data-testid="objection-title"
              className="font-display text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Tu vends. <span className="text-[#ef2b2b]">Tu encaisses 100%.</span>
              <br />
              Sans rien créer.
            </h2>

            <p
              data-testid="objection-body"
              className="text-base leading-relaxed text-zinc-300 sm:text-lg"
            >
              Avec les <span className="font-bold text-white">droits PLR</span>{" "}
              (Private Label Rights), tu prends les 1000+ formations, tu les
              revends sous <span className="font-bold text-white">ton propre nom</span>,
              et tu gardes{" "}
              <span className="font-bold text-[#ef2b2b]">100% des bénéfices</span>.
              Aucune commission. Aucune royalty. Aucune limite.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                {
                  k: "Le contenu est en anglais ?",
                  v: "ChatGPT le traduit en 2 min. Tu deviens le seul vendeur FR sur ton marché.",
                },
                {
                  k: "Marge brute par vente",
                  v: "100%. Pas de frais récurrents, pas de partage de revenus.",
                },
                {
                  k: "Stock & logistique",
                  v: "Zéro. Produits 100% digitaux, livrables à l'infini.",
                },
                {
                  k: "Concurrence FR",
                  v: "Quasi nulle. Le marché français du PLR premium est vide.",
                },
              ].map((row) => (
                <div
                  key={row.k}
                  className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
                >
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#ef2b2b]">
                    {row.k}
                  </div>
                  <div className="mt-1 text-sm text-zinc-200">{row.v}</div>
                </div>
              ))}
            </div>

            <p className="pt-2 font-display text-lg font-bold leading-snug text-white sm:text-xl">
              C'est l'arbitrage business le plus déséquilibré de la décennie.
              <br />
              <span className="text-[#ef2b2b]">
                Ceux qui agissent maintenant prennent le marché.
              </span>
            </p>
          </div>
        </motion.section>

        {/* OUTCOME / MATH SECTION */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="outcome-section"
          className="rounded-2xl border border-white/10 bg-[#121212] p-8 sm:p-10"
        >
          <div className="flex items-center gap-2">
            <TrendUp size={20} weight="duotone" className="text-[#ef2b2b]" />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#ef2b2b]">
              Le calcul que personne ne fait
            </span>
          </div>
          <h3 className="mt-4 font-display text-2xl font-black leading-tight text-white sm:text-3xl">
            1 produit vendu 27€. <span className="text-[#ef2b2b]">×10 par mois.</span>{" "}
            = 270€ MRR.
          </h3>
          <p className="mt-3 text-base leading-relaxed text-zinc-400 sm:text-lg">
            Tu as <span className="font-bold text-white">1000+ produits</span> sous la
            main. Lance-en 5, 10, 50 — chacun devient une mini-source de
            revenus. C'est exactement comme ça que les meilleurs créateurs FR
            ont scalé en 2025.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl border border-white/5 bg-white/[0.02] py-4">
              <div className="font-display text-2xl font-black text-white">5</div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                produits lancés
              </div>
            </div>
            <div className="rounded-xl border border-[#ef2b2b]/20 bg-[#ef2b2b]/5 py-4">
              <div className="font-display text-2xl font-black text-[#ef2b2b]">
                1 350€
              </div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">
                potentiel /mois
              </div>
            </div>
            <div className="rounded-xl border border-white/5 bg-white/[0.02] py-4">
              <div className="font-display text-2xl font-black text-white">100%</div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                pour toi
              </div>
            </div>
          </div>
          <p className="mt-3 text-[11px] italic text-zinc-600">
            Exemple à titre indicatif. Les résultats dépendent de ton marché et de ton travail, et ne sont pas garantis.
          </p>
        </motion.section>

        {/* FEATURES */}
        <section data-testid="features-section" className="flex flex-col gap-1">
          <motion.h3
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-6 text-xs font-bold uppercase tracking-[0.3em] text-zinc-500"
          >
            Ce que tu obtiens
          </motion.h3>
          <ul className="flex flex-col gap-1">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.li
                  key={f.label}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.6 }}
                  variants={fadeUp}
                  data-testid={`feature-item-${i}`}
                  className="group flex items-center gap-4 border-b border-white/5 py-4 last:border-b-0"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#ef2b2b]/25 bg-[#ef2b2b]/10 transition-all group-hover:border-[#ef2b2b]/60 group-hover:bg-[#ef2b2b]/20">
                    <Icon size={18} weight="duotone" className="text-[#ef2b2b]" />
                  </span>
                  <span className="text-base font-medium text-zinc-100 sm:text-lg">
                    {f.label}
                  </span>
                </motion.li>
              );
            })}
          </ul>
        </section>

        {/* WHY PLR — educational, neutral */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="testimonial-section"
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#121212] p-8 sm:p-10"
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#ef2b2b]/10 blur-3xl" />
          <div className="relative flex flex-col gap-5">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#ef2b2b]/30 bg-[#ef2b2b]/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.25em] text-[#ef2b2b]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ef2b2b]" />
              Pourquoi le modèle PLR
            </div>
            <h3 className="font-display text-2xl font-black leading-tight text-white sm:text-3xl">
              Pas de création. Pas de stock.
              <br />
              <span className="text-[#ef2b2b]">
                Tes produits à re-brander, ton nom.
              </span>
            </h3>
            <p className="text-base leading-relaxed text-zinc-300 sm:text-lg">
              Le principe est simple : tu accèdes à une bibliothèque de produits
              digitaux avec <span className="font-bold text-white">droits de
              revente</span>. Tu choisis ce qui correspond à ton marché, tu le
              mets à ton nom, tu le vends.{" "}
              <span className="text-zinc-400">
                Le reste — audience, distribution — c'est ton travail.
              </span>
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { k: "Accès", v: "Bibliothèque complète, à vie" },
                { k: "Personnalisation", v: "Ton nom, ton branding" },
                { k: "Revente", v: "Aucune commission à reverser" },
              ].map((row) => (
                <div
                  key={row.k}
                  className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
                >
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#ef2b2b]">
                    {row.k}
                  </div>
                  <div className="mt-1 text-sm text-zinc-200">{row.v}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* SOCIAL PROOF */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="social-proof"
          className="flex flex-col items-start gap-4 sm:flex-row sm:items-center"
        >
          <div className="flex -space-x-3">
            {AVATARS.map((src, i) => (
              <span
                key={i}
                className="relative inline-block h-10 w-10 overflow-hidden rounded-full ring-2 ring-[#0a0a0a]"
              >
                <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
              </span>
            ))}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1 text-[#ef2b2b]">
              {[...Array(5)].map((_, i) => (
                <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l2.97 6.02L22 9.27l-5 4.87 1.18 6.86L12 17.77l-6.18 3.23L7 14.14 2 9.27l7.03-1.25L12 2z" />
                </svg>
              ))}
            </div>
            <p className="text-sm text-zinc-400 sm:text-base">
              Utilisé par{" "}
              <span className="font-semibold text-white">+20 000 créateurs</span>{" "}
              dans <span className="font-semibold text-white">50 pays</span>
            </p>
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="faq-section"
          className="flex flex-col gap-6"
        >
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#ef2b2b]">
              FAQ
            </span>
            <h3 className="font-display text-3xl font-black leading-tight text-white sm:text-4xl">
              Les questions qu'on nous pose le plus.
            </h3>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {[
              {
                q: "Comment je paie et j'obtiens l'accès ?",
                a: "Paiement sécurisé en une fois via Stripe. Tu reçois ton accès à la bibliothèque par email immédiatement après le paiement. Aucun délai, aucune validation manuelle.",
              },
              {
                q: "Comment utiliser la Master Library ?",
                a: "Tu te connectes à ton dashboard, tu télécharges les produits qui t'intéressent (ebooks, guides, vidéos, courses, prompts IA…), tu personnalises avec ton branding, et tu les vends ou utilises comme tu veux.",
              },
              {
                q: "Y a-t-il des upsells ou le prix est final ?",
                a: "Le prix est final. Paiement unique, accès à vie. Aucune mensualité, aucun frais caché, aucun upsell forcé. Tu paies une fois, tu reçois tout.",
              },
              {
                q: "Je peux modifier, rebrander et vendre comme si c'était à moi ?",
                a: "Oui — c'est exactement le but des droits PLR (Private Label Rights) inclus. Tu peux changer le nom, le logo, le design, le contenu, et le vendre 100% sous ton nom. Tu gardes l'intégralité des bénéfices.",
              },
              {
                q: "Quelle licence et quels droits j'obtiens ?",
                a: "Licence PLR complète : tu peux vendre, modifier, rebrander, offrir en lead magnet, intégrer à des coachings, créer des bundles… Le seul droit non-cédé est la revente des droits PLR eux-mêmes à tes clients.",
              },
              {
                q: "Les produits viennent avec MRR ?",
                a: "Tous les produits incluent les Master Resell Rights (MRR) ET les Private Label Rights (PLR), ce qui te permet de les revendre intégralement et de garder 100% des revenus.",
              },
              {
                q: "Je peux prévisualiser le contenu avant d'acheter ?",
                a: "Oui — accède à un aperçu gratuit de la bibliothèque depuis le site Entrepedia. Tu vois la qualité, les formats, et les sujets disponibles avant de prendre ta décision.",
              },
              {
                q: "À quoi je peux utiliser ces produits digitaux ?",
                a: "Tout : revente directe, freebies pour grossir ta liste email, bonus dans tes formations, ressources pour ta communauté, livres sous ton nom sur Amazon KDP, contenus de coaching, modules pour ta membership… Aucune limite d'usage.",
              },
              {
                q: "Je peux être remboursé si je ne suis pas satisfait ?",
                a: "Aucun problème — tu es protégé par la garantie premium 30 jours. Si la Master Library ne te convient pas, tu contactes le support dans les 30 jours, et tu es remboursé à 100% sur le moyen de paiement initial. Conditions générales applicables.",
              },
              {
                q: "À quelle fréquence la Master Library est mise à jour ?",
                a: "+20 nouveaux produits sont ajoutés chaque semaine. La bibliothèque grossit en permanence avec les tendances et demandes des utilisateurs.",
              },
              {
                q: "Si j'achète maintenant, j'aurai accès aux mises à jour futures ?",
                a: "Oui — accès à vie aux mises à jour. Chaque nouveau produit ajouté à la Master Library est automatiquement disponible dans ton dashboard, sans frais supplémentaires.",
              },
              {
                q: "C'est un abonnement ou un paiement unique ?",
                a: "Paiement unique. Une seule fois, accès à vie. Aucune mensualité, aucun renouvellement, aucune surprise.",
              },
              {
                q: "Je dois avoir des compétences ou de l'expérience pour me lancer ?",
                a: "Non. Les produits sont prêts à l'emploi. Tu peux les vendre tels quels ou les personnaliser. Si tu débutes, tu peux commencer par les utiliser comme lead magnets pour grossir ta liste email avant de monétiser.",
              },
            ].map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-white/10"
              >
                <AccordionTrigger className="py-5 text-left font-display text-base font-bold text-white hover:no-underline sm:text-lg">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-zinc-400 sm:text-base">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.section>

        {/* CTA FORM */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="cta-section"
          className="flex flex-col gap-5 rounded-2xl border border-[#ef2b2b]/30 bg-gradient-to-br from-[#1a0a0a] via-[#121212] to-[#0a0a0a] p-8 shadow-[0_0_80px_-15px_rgba(239,43,43,0.35)] sm:p-10"
        >
          <div className="flex flex-col gap-2">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#ef2b2b]/40 bg-[#ef2b2b]/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.25em] text-[#ef2b2b]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#ef2b2b]" />
              Dernière étape
            </span>
            <h3 className="mt-2 font-display text-3xl font-black leading-tight text-white sm:text-4xl">
              Reçois ton code promo par email.
            </h3>
            <p className="text-sm text-zinc-300 sm:text-base">
              Entre ton email — on t'envoie ton{" "}
              <span className="font-bold text-[#ef2b2b]">code -10% exclusif</span>{" "}
              valable sur ton accès à vie. Reçu en 60 secondes.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="cta-form"
            className="flex flex-col gap-3 sm:flex-row"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              data-testid="email-input"
              className="h-14 flex-1 rounded-xl border border-white/10 bg-[#0a0a0a] px-5 text-base text-white placeholder:text-zinc-500 transition-all focus:border-[#ef2b2b]/60 focus:outline-none focus:ring-2 focus:ring-[#ef2b2b]/30"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-cta"
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-xl bg-[#ef2b2b] px-8 text-sm font-black uppercase tracking-wider text-white transition-all hover:bg-[#dc1f1f] hover:shadow-[0_0_30px_-5px_rgba(239,43,43,0.7)] active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? "Chargement…" : "Recevoir mon code"}
              <ArrowRight
                size={18}
                weight="bold"
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
          </form>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
            <span>✓ Code envoyé instantanément</span>
            <span>✓ Aucun spam</span>
            <span>✓ Désinscription en 1 clic</span>
          </p>

          {/* Risk reversal */}
          <div className="mt-2 flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <ShieldCheck size={28} weight="duotone" className="shrink-0 text-[#ef2b2b]" />
            <div>
              <div className="text-sm font-bold text-white">
                Garantie 30 jours
              </div>
              <div className="text-xs text-zinc-400">
                L'achat se fait directement sur la plateforme officielle Entrepedia,
                qui applique sa propre garantie satisfait ou remboursé sous 30 jours.
                Tu testes la bibliothèque sans risque.
              </div>
            </div>
          </div>
        </motion.section>

        {/* Footer mark */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="border-t border-white/5 pt-8 text-xs uppercase tracking-[0.25em] text-zinc-600"
        >
          © 2026 Entrepedia.fr · Tous droits réservés
        </motion.div>
      </div>

      {/* Sticky mobile CTA */}
      <div
        data-testid="sticky-cta"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-[#ef2b2b]/20 bg-[#0a0a0a]/95 px-4 py-3 backdrop-blur-md sm:hidden"
      >
        <button
          onClick={scrollToCTA}
          className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#ef2b2b] text-sm font-black uppercase tracking-wider text-white shadow-[0_0_30px_-5px_rgba(239,43,43,0.7)]"
        >
          Recevoir mon code -10%
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </main>
  );
}
